/*
 * Pre-Order Delivery Date picker.
 *
 * Every handler is delegated from document so pickers injected after load —
 * quick view, quick add — work without re-initialising anything.
 *
 * Dates are handled as local Y/M/D triples. `new Date('2026-02-15')` parses as
 * UTC and lands on the previous day for anyone behind it, so ISO strings are
 * split manually and rebuilt with new Date(y, m - 1, d).
 */
(function () {
  'use strict';

  if (window.ufcPreorderDatepickerReady) return;
  window.ufcPreorderDatepickerReady = true;

  var NEEDS_DATE_CLASS = 'ufc-preorder-needs-date';

  function parseISO(value) {
    var parts = String(value || '').split('-');
    if (parts.length !== 3) return null;
    var y = parseInt(parts[0], 10);
    var m = parseInt(parts[1], 10);
    var d = parseInt(parts[2], 10);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  }

  function toISO(date) {
    var m = date.getMonth() + 1;
    var d = date.getDate();
    return date.getFullYear() + '-' + (m < 10 ? '0' : '') + m + '-' + (d < 10 ? '0' : '') + d;
  }

  function startOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  function sameDay(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  function config(root) {
    var min = parseISO(root.getAttribute('data-min'));
    var max = parseISO(root.getAttribute('data-max'));
    return {
      min: min,
      max: max,
      locale: root.getAttribute('data-locale') || 'en',
      panel: root.querySelector('[data-ufc-dp-panel]'),
      trigger: root.querySelector('[data-ufc-dp-trigger]'),
      display: root.querySelector('[data-ufc-dp-display]'),
      error: root.querySelector('[data-ufc-dp-error]'),
      input: root.querySelector('[data-ufc-dp-input]'),
      key: root.querySelector('[data-ufc-dp-key]')
    };
  }

  // Composed as "14 Aug 2026" rather than handed wholesale to toLocaleDateString,
  // which under the 'en' locale returns "Aug 14, 2026". Every other pre-order
  // string in the theme comes from Liquid's `%-d %b %Y`, and the two formats
  // sitting side by side on the same product page looked like a bug. The month
  // name is still taken from the locale, so translations keep working.
  function formatLong(date, locale) {
    try {
      var month = date.toLocaleDateString(locale, { month: 'short' });
      return date.getDate() + ' ' + month + ' ' + date.getFullYear();
    } catch (e) {
      return toISO(date);
    }
  }

  function monthLabel(date, locale) {
    try {
      return date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    } catch (e) {
      return date.getFullYear() + '-' + (date.getMonth() + 1);
    }
  }

  // Weekday initials for the calendar header, in the locale's own order
  // (Monday first, which is what the rest of the storefront uses).
  function weekdayNames(locale) {
    var names = [];
    // 2024-01-01 was a Monday, so this walks Mon..Sun.
    for (var i = 0; i < 7; i++) {
      var d = new Date(2024, 0, 1 + i);
      try {
        names.push(d.toLocaleDateString(locale, { weekday: 'short' }));
      } catch (e) {
        names.push(['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'][i]);
      }
    }
    return names;
  }

  // data-min is stamped server side, so it is only correct for the day the page
  // was rendered. A tab left open overnight, or a page restored from bfcache,
  // would otherwise still offer a day that has since passed. Recomputing on every
  // read keeps the lower bound honest for the life of the page.
  //
  // The floor is the order date plus LEAD_DAYS of preparation time. It has to
  // stay in step with the seconds offset in snippets/ufc-preorder-window.liquid
  // (bound: 'from'), or a page left open could offer a day the server rejects.
  var LEAD_DAYS = 2;

  function earliestAllowed() {
    var now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate() + LEAD_DAYS);
  }

  function effectiveMin(cfg) {
    var floor = earliestAllowed();
    if (!cfg.min) return floor;
    return cfg.min > floor ? cfg.min : floor;
  }

  function clampToRange(date, cfg) {
    var min = effectiveMin(cfg);
    if (date < min) return new Date(min.getTime());
    if (cfg.max && date > cfg.max) return new Date(cfg.max.getTime());
    return date;
  }

  function isSelectable(date, cfg) {
    if (date < effectiveMin(cfg)) return false;
    if (cfg.max && date > startOfDay(cfg.max)) return false;
    return true;
  }

  function render(root, viewDate) {
    var cfg = config(root);
    if (!cfg.panel || !cfg.min || !cfg.max) return;

    var view = startOfDay(viewDate);
    var selected = parseISO(cfg.key ? cfg.key.value : '');

    var firstOfMonth = new Date(view.getFullYear(), view.getMonth(), 1);
    var daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();

    // getDay() is Sunday-indexed; shift so Monday starts the row.
    var lead = (firstOfMonth.getDay() + 6) % 7;

    var minMonth = effectiveMin(cfg);
    var prevDate = new Date(view.getFullYear(), view.getMonth() - 1, 1);
    var nextDate = new Date(view.getFullYear(), view.getMonth() + 1, 1);
    var prevAllowed = new Date(minMonth.getFullYear(), minMonth.getMonth(), 1) <= prevDate;
    var nextAllowed = new Date(cfg.max.getFullYear(), cfg.max.getMonth(), 1) >= nextDate;

    var html = '';
    html += '<div class="ufc-dp__head">';
    html += '<button type="button" class="ufc-dp__nav" data-ufc-dp-nav="-1" aria-label="Previous month"' + (prevAllowed ? '' : ' disabled') + '>&#8249;</button>';
    html += '<span class="ufc-dp__month" data-ufc-dp-month>' + monthLabel(view, cfg.locale) + '</span>';
    html += '<button type="button" class="ufc-dp__nav" data-ufc-dp-nav="1" aria-label="Next month"' + (nextAllowed ? '' : ' disabled') + '>&#8250;</button>';
    html += '</div>';

    html += '<div class="ufc-dp__grid ufc-dp__grid--head" aria-hidden="true">';
    weekdayNames(cfg.locale).forEach(function (n) {
      html += '<span class="ufc-dp__wd">' + n + '</span>';
    });
    html += '</div>';

    html += '<div class="ufc-dp__grid" role="grid">';
    for (var i = 0; i < lead; i++) html += '<span class="ufc-dp__pad"></span>';

    for (var day = 1; day <= daysInMonth; day++) {
      var d = new Date(view.getFullYear(), view.getMonth(), day);
      var ok = isSelectable(d, cfg);
      var isSel = selected && sameDay(d, selected);
      html += '<button type="button" class="ufc-dp__day' + (isSel ? ' is-selected' : '') + '"';
      html += ' data-ufc-dp-day="' + toISO(d) + '"';
      html += ' role="gridcell" tabindex="' + (isSel ? '0' : '-1') + '"';
      html += ' aria-selected="' + (isSel ? 'true' : 'false') + '"';
      html += ' aria-label="' + formatLong(d, cfg.locale) + '"';
      html += ok ? '' : ' disabled aria-disabled="true"';
      html += '>' + day + '</button>';
    }
    html += '</div>';

    cfg.panel.innerHTML = html;
    cfg.panel.setAttribute('data-view', toISO(view));
  }

  function open(root) {
    var cfg = config(root);
    if (!cfg.panel || !cfg.min || !cfg.max) return;

    closeAll(root);

    var selected = parseISO(cfg.key ? cfg.key.value : '');
    var view = selected || clampToRange(startOfDay(new Date()), cfg);

    render(root, view);
    cfg.panel.hidden = false;
    if (cfg.trigger) cfg.trigger.setAttribute('aria-expanded', 'true');

    var focusTarget = cfg.panel.querySelector('.ufc-dp__day[tabindex="0"]') ||
      cfg.panel.querySelector('.ufc-dp__day:not([disabled])');
    if (focusTarget) focusTarget.focus();
  }

  function close(root) {
    var cfg = config(root);
    if (!cfg.panel) return;
    cfg.panel.hidden = true;
    if (cfg.trigger) cfg.trigger.setAttribute('aria-expanded', 'false');
  }

  function closeAll(except) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-ufc-dp]'), function (root) {
      if (root !== except) close(root);
    });
  }

  function select(root, iso) {
    var cfg = config(root);
    var date = parseISO(iso);
    if (!date || !isSelectable(date, cfg)) return;

    if (cfg.input) cfg.input.value = formatLong(date, cfg.locale);
    if (cfg.key) cfg.key.value = toISO(date);
    if (cfg.display) cfg.display.textContent = formatLong(date, cfg.locale);
    if (cfg.error) cfg.error.hidden = true;

    root.classList.add('has-value');

    var form = root.closest('form');
    if (form) form.classList.remove(NEEDS_DATE_CLASS);

    close(root);
    if (cfg.trigger) cfg.trigger.focus();
  }

  // A form is only blocked when it actually contains a picker, so ordinary
  // products are never affected.
  //
  // The stored value is re-validated rather than just checked for emptiness. It
  // can go stale on its own — a tab left open past midnight pushes the lower
  // bound forward under a selection that was legitimate when it was made — and
  // a hidden input is trivially editable from devtools.
  function unmetDate(form) {
    if (!form) return null;
    var root = form.querySelector('[data-ufc-dp]');
    if (!root) return null;

    var cfg = config(root);
    if (!cfg.key || !cfg.key.value) return root;

    var chosen = parseISO(cfg.key.value);
    if (!chosen || !isSelectable(chosen, cfg)) return root;

    return null;
  }

  function clearSelection(root) {
    var cfg = config(root);
    if (cfg.key) cfg.key.value = '';
    if (cfg.input) cfg.input.value = '';
    if (cfg.display) cfg.display.textContent = cfg.display.getAttribute('data-placeholder') || '';
    root.classList.remove('has-value');
  }

  function reject(root) {
    var cfg = config(root);

    // Drop a selection that is no longer valid so the trigger cannot keep
    // showing a date the customer can no longer actually have.
    if (cfg.key && cfg.key.value) {
      var chosen = parseISO(cfg.key.value);
      if (!chosen || !isSelectable(chosen, cfg)) clearSelection(root);
    }

    if (cfg.error) cfg.error.hidden = false;
    var form = root.closest('form');
    if (form) form.classList.add(NEEDS_DATE_CLASS);
    open(root);
    if (root.scrollIntoView) root.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-ufc-dp-trigger]');
    if (trigger) {
      e.preventDefault();
      var root = trigger.closest('[data-ufc-dp]');
      var cfg = config(root);
      if (cfg.panel && cfg.panel.hidden) open(root);
      else close(root);
      return;
    }

    var nav = e.target.closest('[data-ufc-dp-nav]');
    if (nav) {
      e.preventDefault();
      if (nav.disabled) return;
      var navRoot = nav.closest('[data-ufc-dp]');
      var panel = navRoot.querySelector('[data-ufc-dp-panel]');
      var current = parseISO(panel.getAttribute('data-view')) || new Date();
      var step = parseInt(nav.getAttribute('data-ufc-dp-nav'), 10);
      render(navRoot, new Date(current.getFullYear(), current.getMonth() + step, 1));
      var refocus = panel.querySelector('.ufc-dp__nav[data-ufc-dp-nav="' + step + '"]');
      if (refocus && !refocus.disabled) refocus.focus();
      return;
    }

    var day = e.target.closest('[data-ufc-dp-day]');
    if (day) {
      e.preventDefault();
      if (day.disabled) return;
      select(day.closest('[data-ufc-dp]'), day.getAttribute('data-ufc-dp-day'));
      return;
    }

    if (!e.target.closest('[data-ufc-dp]')) closeAll(null);
  });

  // Capture phase so this runs before the theme's own submit handler, which is
  // bound on the form itself. Without capture the AJAX add fires first.
  document.addEventListener('submit', function (e) {
    var root = unmetDate(e.target);
    if (!root) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    reject(root);
  }, true);

  // The add to cart button is not given the disabled attribute, because the
  // theme's variant logic owns that and would fight over it. Blocking the click
  // here keeps the two concerns separate.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-add-to-cart]');
    if (!btn) return;
    var root = unmetDate(btn.closest('form'));
    if (!root) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    reject(root);
  }, true);

  document.addEventListener('keydown', function (e) {
    var root = e.target.closest('[data-ufc-dp]');
    if (!root) return;

    if (e.key === 'Escape') {
      close(root);
      var cfg = config(root);
      if (cfg.trigger) cfg.trigger.focus();
      return;
    }

    var day = e.target.closest('[data-ufc-dp-day]');
    if (!day) return;

    var offsets = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    var offset = offsets[e.key];
    if (!offset) return;

    e.preventDefault();
    var cur = parseISO(day.getAttribute('data-ufc-dp-day'));
    if (!cur) return;

    var next = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + offset);
    var cfgK = config(root);
    if (!isSelectable(next, cfgK)) return;

    render(root, next);
    var target = root.querySelector('[data-ufc-dp-day="' + toISO(next) + '"]');
    if (target) {
      target.setAttribute('tabindex', '0');
      target.focus();
    }
  });
})();
